/**
 * Global error handling utilities
 * Shows appropriate toasts for different error types and logs to Datadog
 */

import { CanceledError, isAxiosError, type AxiosError } from 'axios';
import { addToast } from '@teamworksdev/vuetify';
import { i18n } from '@/plugins/i18n';
import { datadogLogs } from '@datadog/browser-logs';

const { t } = i18n.global;
const isDev = import.meta.env.DEV;

/**
 * Extracts only the safe-to-log fields from an AxiosError, avoiding exposure
 * of request/response payloads and headers that may contain sensitive data.
 */
function getAxiosErrorLogDetails(error: AxiosError) {
  return {
    message: error.message,
    status: error.response?.status,
    code: error.code,
  };
}

/**
 * Handle Axios-specific error codes (network errors, timeouts, etc.)
 */
function handleAxiosErrorCode(error: AxiosError, code?: string): void {
  if (!code) return;

  const axiosErrorHandlers: Record<string, () => void> = {
    // Request was cancelled - typically intentional, so don't show error
    ERR_CANCELED: () => {},

    // Network error - no internet connection or server unreachable
    ERR_NETWORK: () => {
      if (isDev) {
        console.warn('[ErrorHandler] Network error', getAxiosErrorLogDetails(error));
      }
      addToast({
        type: 'error',
        title: t('toast.errors.network.title'),
        description: t('toast.errors.network.description'),
      });
    },

    // Timeout error
    ECONNABORTED: () => {
      if (isDev) {
        console.warn('[ErrorHandler] Timeout error', getAxiosErrorLogDetails(error));
      }
      addToast({
        type: 'error',
        title: t('toast.errors.timeout.title'),
        description: t('toast.errors.timeout.description'),
      });
    },
  };

  axiosErrorHandlers[code]?.();
}

/**
 * Handle HTTP status codes
 */
function handleHttpStatusCode(
  error: AxiosError,
  status: number,
  customToast?: {
    title?: string;
    description?: string;
  },
): void {
  // If custom toast title/description provided, use them instead of status-specific ones
  if (customToast?.title || customToast?.description) {
    if (isDev) {
      console.warn('[ErrorHandler] HTTP error with custom toast', getAxiosErrorLogDetails(error));
    }
    addToast({
      type: 'error',
      title: customToast.title || t('toast.errors.default.title'),
      description: customToast.description || t('toast.errors.default.description'),
    });
    return;
  }
  // Only handle specific status codes with custom messages
  // Everything else gets the default error message
  const statusHandlers: Record<number, () => void> = {
    // Unauthorized - token expired or invalid
    401: () => {
      // Don't show toast for 401 - let auth system handle it
      // The auth system will redirect to login
      if (isDev) {
        console.warn('[ErrorHandler] 401 Unauthorized - redirecting to login', getAxiosErrorLogDetails(error));
      }
    },

    // Not Found
    404: () => {
      if (isDev) {
        console.warn('[ErrorHandler] 404 Not Found', getAxiosErrorLogDetails(error));
      }
      addToast({
        type: 'error',
        title: t('toast.errors.404.title'),
        description: t('toast.errors.404.description'),
      });
    },

    // Gateway Timeout
    504: () => {
      if (isDev) {
        console.warn('[ErrorHandler] 504 Gateway Timeout', getAxiosErrorLogDetails(error));
      }
      addToast({
        type: 'error',
        title: t('toast.errors.504.title'),
        description: t('toast.errors.504.description'),
      });
    },
  };

  if (statusHandlers[status]) {
    statusHandlers[status]();
  } else {
    // All other status codes - show generic error
    if (isDev) {
      console.warn('[ErrorHandler] Unknown HTTP status error', getAxiosErrorLogDetails(error));
    }
    addToast({
      type: 'error',
      title: t('toast.errors.default.title'),
      description: t('toast.errors.default.description'),
    });
  }
}

/**
 * Main error handler function
 * Shows appropriate toast and logs to Datadog
 *
 * @param error - The error object to handle
 * @param customToast - Optional custom toast configuration
 */
export function handleError(error: unknown, customToast?: {
  title?: string;
  description?: string;
}): void {
  // Don't handle cancelled requests
  if (error instanceof CanceledError) {
    throw error;
  }

  // Handle Axios errors
  if (isAxiosError(error)) {
    if (isDev) {
      console.warn('[ErrorHandler] Axios error', getAxiosErrorLogDetails(error));
    }

    if (error.response) {
      // Error with response (HTTP status code)
      handleHttpStatusCode(error, error.response.status, customToast);
    } else if (customToast?.title || customToast?.description) {
      // Network error with custom toast
      if (isDev) {
        console.warn('[ErrorHandler] Axios network error with custom toast', getAxiosErrorLogDetails(error));
      }
      addToast({
        type: 'error',
        title: customToast.title || t('toast.errors.default.title'),
        description: customToast.description || t('toast.errors.default.description'),
      });
    } else {
      // Network error (no response)
      handleAxiosErrorCode(error, error.code);
    }

    // Log to Datadog in production
    if (!isDev) {
      datadogLogs.logger.error(error.message, {
        error: {
          stack: error.stack,
          message: error.message,
        },
        status: error.response?.status,
        code: error.code,
        url: error.config?.url,
        method: error.config?.method,
      });
    }
  } else if (error instanceof Error) {
    // Generic JavaScript error
    if (isDev) {
      console.warn('[ErrorHandler] JavaScript error', error);
    }

    addToast({
      type: 'error',
      title: customToast?.title || t('toast.errors.default.title'),
      description: customToast?.description || t('toast.errors.default.description'),
    });

    if (!isDev) {
      datadogLogs.logger.error(error.message, {
        error: {
          stack: error.stack,
          message: error.message,
        },
      });
    }
  } else {
    // Unknown error type
    if (isDev) {
      console.warn('[ErrorHandler] Unknown error type', error);
    }

    addToast({
      type: 'error',
      title: customToast?.title || t('toast.errors.default.title'),
      description: customToast?.description || t('toast.errors.default.description'),
    });

    if (!isDev) {
      datadogLogs.logger.error('Unknown error type', { error: String(error) });
    }
  }
}
